import type { InputHTMLAttributes, ReactElement, ReactNode, Ref } from 'react';

import { classNames } from '@m1work/shared-utils';

import styles from './Input.module.scss';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
    /** Show the error state (red border). @default false */
    error?: boolean;
    /** Content inside the field before the text, e.g. an icon. */
    left?: ReactNode;
    /** Content inside the field after the text, e.g. a button. */
    right?: ReactNode;
    ref?: Ref<HTMLInputElement>;
}

/**
 * A text field with optional content on either side. Wrap it in `FormGroup` for a label and an error message.
 * `className` goes to the wrapper; all other props go to the `<input>`.
 *
 * @example
 * <Input placeholder={t('search.placeholder')} left={<SearchIcon />} error={Boolean(errors.query)} />
 */
export function Input({ error = false, left, right, className, ref, ...props }: InputProps): ReactElement {
    return (
        <div className={classNames(styles.wrapper, error && styles.error, className)}>
            {left && <div className={styles.slot}>{left}</div>}
            <input ref={ref} className={styles.input} {...props} />
            {right && <div className={styles.slot}>{right}</div>}
        </div>
    );
}
